import React, { useState, useCallback } from 'react';
import { Upload, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAdminAuth } from '../context/AdminAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BulkImageUpload = ({ category, onUploadComplete, onClose }) => {
  const { getAuthHeader } = useAdminAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      file => file.type.startsWith('image/')
    );
    
    setFiles(prev => [
      ...prev,
      ...droppedFiles.map(file => ({
        file,
        name: file.name,
        preview: URL.createObjectURL(file),
        status: 'pending',
        title: file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ')
      }))
    ]);
  }, []);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files).filter(
      file => file.type.startsWith('image/')
    );
    
    setFiles(prev => [
      ...prev,
      ...selectedFiles.map(file => ({
        file,
        name: file.name,
        preview: URL.createObjectURL(file),
        status: 'pending',
        title: file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ')
      }))
    ]);
  };

  const removeFile = (index) => {
    setFiles(prev => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const updateFileTitle = (index, title) => {
    setFiles(prev => {
      const newFiles = [...prev];
      newFiles[index].title = title;
      return newFiles;
    });
  };

  const uploadAll = async () => {
    setUploading(true);
    const today = new Date().toISOString().split('T')[0];
    
    for (let i = 0; i < files.length; i++) {
      if (files[i].status === 'success') continue;
      
      setFiles(prev => {
        const newFiles = [...prev];
        newFiles[i].status = 'uploading';
        return newFiles;
      });

      try {
        // Upload image first
        const formData = new FormData();
        formData.append('file', files[i].file);
        
        const uploadResponse = await axios.post(
          `${API_URL}/api/upload/image`,
          formData,
          { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
        );

        // Create gallery entry
        const imageUrl = uploadResponse.data.url.startsWith('http') 
          ? uploadResponse.data.url 
          : `${API_URL}${uploadResponse.data.url}`;

        await axios.post(
          `${API_URL}/api/gallery`,
          {
            title: files[i].title,
            category: category,
            image: imageUrl,
            date: today
          },
          { headers: getAuthHeader() }
        );

        setFiles(prev => {
          const newFiles = [...prev];
          newFiles[i].status = 'success';
          return newFiles;
        });
      } catch (error) {
        console.error('Upload error:', error);
        setFiles(prev => {
          const newFiles = [...prev];
          newFiles[i].status = 'error';
          newFiles[i].error = error.response?.data?.detail || 'Upload failed';
          return newFiles;
        });
      }
    }

    setUploading(false);
    
    // Check if all uploads succeeded
    const allSuccess = files.every(f => f.status === 'success');
    if (allSuccess && onUploadComplete) {
      setTimeout(() => onUploadComplete(), 1000);
    }
  };

  const successCount = files.filter(f => f.status === 'success').length;
  const pendingCount = files.filter(f => f.status === 'pending' || f.status === 'error').length;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Bulk Image Upload</h2>
            <p className="text-gray-500 mt-1">Category: <span className="font-semibold text-purple-600">{category}</span></p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Drop Zone */}
        <div className="p-6">
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
              isDragging
                ? 'border-purple-500 bg-purple-50'
                : 'border-gray-300 hover:border-purple-400 hover:bg-gray-50'
            }`}
          >
            <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-purple-500' : 'text-gray-400'}`} />
            <p className="text-lg font-medium text-gray-700 mb-2">
              Drag & drop images here
            </p>
            <p className="text-gray-500 mb-4">or</p>
            <label className="inline-block px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg cursor-pointer transition-colors">
              Browse Files
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
            <p className="text-sm text-gray-400 mt-4">
              Supports: JPG, PNG, GIF, WebP (auto-resized)
            </p>
          </div>
        </div>

        {/* File List */}
        {files.length > 0 && (
          <div className="px-6 pb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-900">
                {files.length} image{files.length !== 1 ? 's' : ''} selected
                {successCount > 0 && (
                  <span className="ml-2 text-green-600">({successCount} uploaded)</span>
                )}
              </h3>
              {pendingCount > 0 && (
                <button
                  onClick={uploadAll}
                  disabled={uploading}
                  className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white font-semibold rounded-lg flex items-center space-x-2 transition-colors"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      <span>Upload All</span>
                    </>
                  )}
                </button>
              )}
            </div>

            <div className="max-h-64 overflow-y-auto space-y-3">
              {files.map((file, index) => (
                <div
                  key={index}
                  className={`flex items-center space-x-4 p-3 rounded-lg border ${
                    file.status === 'success'
                      ? 'bg-green-50 border-green-200'
                      : file.status === 'error'
                      ? 'bg-red-50 border-red-200'
                      : file.status === 'uploading'
                      ? 'bg-purple-50 border-purple-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  {/* Preview */}
                  <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-200">
                    <img src={file.preview} alt="" className="w-full h-full object-cover" />
                  </div>

                  {/* Title Input */}
                  <div className="flex-1 min-w-0">
                    <input
                      type="text"
                      value={file.title}
                      onChange={(e) => updateFileTitle(index, e.target.value)}
                      disabled={file.status === 'success' || file.status === 'uploading'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                      placeholder="Image title"
                    />
                    {file.error && (
                      <p className="text-xs text-red-600 mt-1">{file.error}</p>
                    )}
                  </div>

                  {/* Status/Actions */}
                  <div className="flex-shrink-0">
                    {file.status === 'success' && (
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    )}
                    {file.status === 'error' && (
                      <AlertCircle className="w-6 h-6 text-red-500" />
                    )}
                    {file.status === 'uploading' && (
                      <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
                    )}
                    {file.status === 'pending' && (
                      <button
                        onClick={() => removeFile(index)}
                        className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5 text-gray-500" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end space-x-4 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors"
          >
            {successCount === files.length && files.length > 0 ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkImageUpload;
